import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ModalShell } from '@/presentation/components/common';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import { InvoiceDocument, buildPrintHtml, formatPeriodLabel } from './ClinicInvoiceDocument';
import { downloadHtmlAsPdf, invoicePdfFilename } from '@/shared/pdf/downloadHtmlPdf';
import { diffInvoiceLines, type InvoiceLineChange } from '@/shared/billing/invoiceLineDiff';

export type ClinicInvoiceLineKind =
  | 'plan'
  | 'consultorio_extra'
  | 'profesional_extra'
  | 'conexion'
  | 'whatsapp_pack'
  | 'whatsapp_overage'
  | 'marketing'
  | 'bot'
  | 'email'
  | 'other';

export type ClinicInvoiceStatus = 'suggested' | 'draft' | 'confirmed';

export type ClinicInvoiceLine = {
  kind: ClinicInvoiceLineKind;
  name: string;
  description: string | null;
  quantity: number;
  unitPrice: number;
  subtotal: number;
};

export type ClinicInvoiceAuditedChange = InvoiceLineChange<ClinicInvoiceLine> & {
  reason: string;
  createdAt?: string | null;
  createdBy?: string | null;
};

export type ClinicInvoiceDTO = {
  tenantId: string;
  nombre: string;
  email: string | null;
  telefono: string | null;
  estado: string;
  period: string;
  dueDate: string | null;
  generatedAt: string;
  isPreview: boolean;
  panelPagoId: number | null;
  paymentStatus: string | null;
  status: ClinicInvoiceStatus;
  confirmedAt: string | null;
  confirmedBy: string | null;
  plan: {
    id: number | null;
    name: string;
    price: number;
    includedConsultorios: number;
    includedProfesionales: number;
  };
  extras: {
    consultorios: { quantity: number; unitPrice: number; subtotal: number };
    profesionales: { quantity: number; unitPrice: number; subtotal: number };
  };
  lines: ClinicInvoiceLine[];
  suggestedLines: ClinicInvoiceLine[];
  changes?: ClinicInvoiceAuditedChange[];
  whatsapp: {
    contracted: number;
    bonus: number;
    quota: number;
    used: number;
    remaining: number;
    usedPct: number;
    overageMessages: number;
    overageAmount: number;
    conexionPrice: number;
  };
  offices: {
    officeId: number | null;
    officeName: string;
    messages: number;
    usagePct: number;
    consultorios?: number;
    extraConsultorios?: number;
    profesionales?: number;
    extraProfesionales?: number;
  }[];
  staffing?: {
    consultorios: number;
    extraConsultorios: number;
    profesionales: number;
    extraProfesionales: number;
  };
  total: number;
  previousDebt: number;
  amountDue: number;
};

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function extrasFromLines(lines: ClinicInvoiceLine[]): ClinicInvoiceDTO['extras'] {
  const empty = { quantity: 0, unitPrice: 0, subtotal: 0 };
  const extras = { consultorios: { ...empty }, profesionales: { ...empty } };
  for (const line of lines) {
    if (line.kind === 'consultorio_extra') {
      extras.consultorios.quantity += line.quantity;
      extras.consultorios.unitPrice = line.unitPrice;
      extras.consultorios.subtotal = round2(extras.consultorios.subtotal + line.subtotal);
    }
    if (line.kind === 'profesional_extra') {
      extras.profesionales.quantity += line.quantity;
      extras.profesionales.unitPrice = line.unitPrice;
      extras.profesionales.subtotal = round2(extras.profesionales.subtotal + line.subtotal);
    }
  }
  return extras;
}

function withSubtotals(lines: ClinicInvoiceLine[]): ClinicInvoiceLine[] {
  return lines.map((line) => ({
    ...line,
    subtotal: round2(Math.max(0, line.quantity) * line.unitPrice),
  }));
}

function formatMoney(value: number, locale: string): string {
  return `$ ${value.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function normalizeWhatsAppPhone(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 8) return null;
  if (digits.startsWith('54')) return digits;
  if (digits.startsWith('0')) return `54${digits.replace(/^0+/, '')}`;
  return `54${digits}`;
}

function apiMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { message?: unknown } } })?.response?.data;
  return typeof data?.message === 'string' && data.message.trim() ? data.message : fallback;
}

function buildDisplayInvoice(invoice: ClinicInvoiceDTO, lines: ClinicInvoiceLine[]): ClinicInvoiceDTO {
  const next = withSubtotals(lines);
  const total = round2(next.reduce((s, l) => s + l.subtotal, 0));
  return {
    ...invoice,
    lines: next,
    extras: extrasFromLines(next),
    total,
    amountDue: round2(total + invoice.previousDebt),
  };
}

function buildWhatsAppText(
  invoice: ClinicInvoiceDTO,
  locale: string,
  t: (k: string, o?: Record<string, unknown>) => string
): string {
  const periodLabel = formatPeriodLabel(invoice.period, locale);
  const due = invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString(locale) : '—';
  const lines = invoice.lines
    .map((line) => `• ${line.name} (${line.quantity}) — ${formatMoney(line.subtotal, locale)}`)
    .join('\n');
  return [
    t('pages.clinicInvoices.waTitle', { clinic: invoice.nombre }),
    `${t('pages.clinicInvoices.period')}: ${periodLabel}`,
    `${t('pages.clinicInvoices.dueDate')}: ${due}`,
    '',
    t('pages.clinicInvoices.concepts'),
    lines,
    '',
    t('pages.clinicInvoices.whatsappTitle'),
    `${t('pages.clinicInvoices.waUsed')}: ${invoice.whatsapp.used.toLocaleString(locale)} / ${invoice.whatsapp.quota.toLocaleString(locale)} (${invoice.whatsapp.usedPct.toFixed(1)}%)`,
    '',
    t('pages.clinicInvoices.byOffice'),
    (invoice.offices ?? [])
      .map(
        (o) =>
          `• ${o.officeName}: ${o.messages.toLocaleString(locale)} (${o.usagePct.toFixed(1)}%)`
      )
      .join('\n') || t('pages.clinicInvoices.noOfficeUsage'),
    '',
    `${t('pages.clinicInvoices.invoiceTotal')}: ${formatMoney(invoice.total, locale)}`,
    invoice.previousDebt > 0
      ? `${t('pages.clinicInvoices.previousDebt')}: ${formatMoney(invoice.previousDebt, locale)}`
      : '',
    `${t('pages.clinicInvoices.amountDue')}: ${formatMoney(invoice.amountDue, locale)}`,
  ]
    .filter((row) => row !== '')
    .join('\n');
}


interface ClinicInvoiceModalProps {
  tenantId: string;
  period: string;
  clinicName?: string;
  onClose: () => void;
  onChanged?: () => void;
}

export function ClinicInvoiceModal({
  tenantId,
  period,
  clinicName,
  onClose,
  onChanged,
}: ClinicInvoiceModalProps) {
  const { t, i18n } = useTranslation();
  const { success, error: showError } = useToast();
  const [invoice, setInvoice] = useState<ClinicInvoiceDTO | null>(null);
  const [draftLines, setDraftLines] = useState<ClinicInvoiceLine[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busy, setBusy] = useState<'save' | 'confirm' | 'restore' | 'reopen' | 'pdf' | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<InvoiceLineChange<ClinicInvoiceLine>[]>([]);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [reviewError, setReviewError] = useState('');

  const applyInvoice = (next: ClinicInvoiceDTO) => {
    setInvoice(next);
    setDraftLines(next.lines.map((line) => ({ ...line })));
    setReviewing(false);
    setPendingChanges([]);
    setReasons({});
    setReviewError('');
  };

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    void httpClient
      .get<ClinicInvoiceDTO>(`/panel-pagos/factura/${encodeURIComponent(tenantId)}`, {
        params: { periodo: period },
      })
      .then((res) => {
        if (!cancelled) applyInvoice(res.data);
      })
      .catch((err) => {
        if (!cancelled) showError(apiMessage(err, t('pages.clinicInvoices.loadError')));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, period, showError, t]);

  const display = useMemo(
    () => (invoice ? buildDisplayInvoice(invoice, draftLines) : null),
    [invoice, draftLines]
  );

  const confirmed = invoice?.status === 'confirmed';
  const periodLabel = formatPeriodLabel(period, i18n.language);

  const waLink = useMemo(() => {
    if (!display?.telefono || !confirmed) return null;
    const phone = normalizeWhatsAppPhone(display.telefono);
    if (!phone) return null;
    const text = buildWhatsAppText(display, i18n.language, t);
    return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
  }, [display, confirmed, i18n.language, t]);

  const payloadLines = () =>
    withSubtotals(draftLines)
      .filter((line) => line.name.trim().length > 0)
      .map((line) => ({
        ...line,
        name: line.name.trim(),
        description: line.description?.trim() || null,
      }));

  const runAction = async (kind: NonNullable<typeof busy>, request: () => Promise<ClinicInvoiceDTO>) => {
    setBusy(kind);
    try {
      const next = await request();
      applyInvoice(next);
      onChanged?.();
      if (kind === 'save') success(t('pages.clinicInvoices.saved'));
      if (kind === 'confirm') success(t('pages.clinicInvoices.confirmed'));
      if (kind === 'restore') success(t('pages.clinicInvoices.restored'));
      if (kind === 'reopen') success(t('pages.clinicInvoices.reopened'));
    } catch (err) {
      showError(apiMessage(err, t('pages.clinicInvoices.saveError')));
    } finally {
      setBusy(null);
    }
  };

  const handleSave = () =>
    runAction('save', async () => {
      const res = await httpClient.put<ClinicInvoiceDTO>(
        `/panel-pagos/factura/${encodeURIComponent(tenantId)}`,
        { periodo: period, lines: payloadLines() }
      );
      return res.data;
    });

  const handleConfirm = (changes: Array<{ id: string; type: string; reason: string }>) =>
    runAction('confirm', async () => {
      const res = await httpClient.post<ClinicInvoiceDTO>(
        `/panel-pagos/factura/${encodeURIComponent(tenantId)}/confirmar`,
        { periodo: period, lines: payloadLines(), changes }
      );
      return res.data;
    });

  const startConfirm = () => {
    if (!invoice || busy) return;
    const diffs = diffInvoiceLines(invoice.suggestedLines, payloadLines());
    if (diffs.length === 0) {
      void handleConfirm([]);
      return;
    }
    setPendingChanges(diffs);
    setReasons((prev) => {
      const next = { ...prev };
      for (const change of diffs) {
        if (!(change.id in next)) next[change.id] = '';
      }
      return next;
    });
    setReviewError('');
    setReviewing(true);
  };

  const submitReview = () => {
    const missing = pendingChanges.some((change) => !(reasons[change.id] ?? '').trim());
    if (missing) {
      setReviewError(t('pages.clinicInvoices.reviewReasonRequired'));
      return;
    }
    void handleConfirm(
      pendingChanges.map((change) => ({
        id: change.id,
        type: change.type,
        reason: (reasons[change.id] ?? '').trim(),
      }))
    );
  };

  const allReasonsFilled = pendingChanges.every((change) => (reasons[change.id] ?? '').trim().length > 0);

  const handleRestore = () =>
    runAction('restore', async () => {
      const res = await httpClient.post<ClinicInvoiceDTO>(
        `/panel-pagos/factura/${encodeURIComponent(tenantId)}/restablecer`,
        { periodo: period }
      );
      return res.data;
    });

  const handleReopen = () =>
    runAction('reopen', async () => {
      const res = await httpClient.post<ClinicInvoiceDTO>(
        `/panel-pagos/factura/${encodeURIComponent(tenantId)}/reabrir`,
        { periodo: period }
      );
      return res.data;
    });

  const handleExportPdf = async () => {
    if (!display || !confirmed || busy) return;
    setBusy('pdf');
    try {
      await downloadHtmlAsPdf(
        buildPrintHtml(display, i18n.language, t),
        invoicePdfFilename(display.nombre, display.period)
      );
    } catch {
      showError(t('pages.clinicInvoices.exportError'));
    } finally {
      setBusy(null);
    }
  };

  const handleCopy = async () => {
    if (!display || !confirmed) return;
    try {
      await navigator.clipboard.writeText(buildWhatsAppText(display, i18n.language, t));
      success(t('pages.clinicInvoices.copied'));
    } catch {
      showError(t('pages.clinicInvoices.copyError'));
    }
  };

  return (
    <ModalShell
      isOpen
      onClose={onClose}
      size="full"
      title={reviewing ? t('pages.clinicInvoices.reviewTitle') : t('pages.clinicInvoices.docTitle')}
      subtitle={`${clinicName || invoice?.nombre || ''} · ${periodLabel}`}
      footerClassName="sm:justify-stretch"
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {reviewing ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setReviewing(false);
                  setReviewError('');
                }}
                disabled={busy !== null}
              >
                {t('pages.clinicInvoices.reviewBack')}
              </Button>
            ) : confirmed ? (
              <Button variant="secondary" onClick={() => void handleReopen()} disabled={busy !== null}>
                {t('pages.clinicInvoices.reopen')}
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => void handleRestore()} disabled={busy !== null}>
                {t('pages.clinicInvoices.restore')}
              </Button>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              {t('common.close')}
            </Button>
            {reviewing ? (
              <Button
                onClick={submitReview}
                disabled={busy !== null || !allReasonsFilled}
                isLoading={busy === 'confirm'}
              >
                {t('pages.clinicInvoices.reviewConfirm')}
              </Button>
            ) : confirmed ? (
              <>
                <Button variant="secondary" onClick={() => void handleCopy()}>
                  {t('pages.clinicInvoices.copy')}
                </Button>
                {waLink ? (
                  <a href={waLink} target="_blank" rel="noreferrer">
                    <Button variant="secondary">{t('pages.clinicInvoices.sendWhatsApp')}</Button>
                  </a>
                ) : null}
                <Button onClick={() => void handleExportPdf()} disabled={busy !== null} isLoading={busy === 'pdf'}>
                  {t('pages.clinicInvoices.print')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  onClick={() => void handleSave()}
                  disabled={busy !== null}
                  isLoading={busy === 'save'}
                >
                  {t('pages.clinicInvoices.saveDraft')}
                </Button>
                <Button onClick={startConfirm} disabled={busy !== null} isLoading={busy === 'confirm'}>
                  {t('pages.clinicInvoices.confirm')}
                </Button>
              </>
            )}
          </div>
        </div>
      }
    >
      {isLoading ? (
        <p className="p-4 text-sm text-slate-500 dark:text-slate-400">{t('common.loading')}</p>
      ) : !display ? (
        <p className="p-4 text-sm text-rose-600">{t('pages.clinicInvoices.loadError')}</p>
      ) : (
        <div className="bg-slate-100 px-1 py-4 sm:px-4">
        {reviewing ? (
          <InvoiceChangeReview
            changes={pendingChanges}
            reasons={reasons}
            error={reviewError}
            locale={i18n.language}
            t={t}
            onReason={(id, value) => {
              setReasons((prev) => ({ ...prev, [id]: value }));
              if (reviewError) setReviewError('');
            }}
          />
        ) : (
          <>
            <InvoiceDocument
              invoice={display}
              editing={!confirmed}
              t={t}
              locale={i18n.language}
              onChangeLine={(index, patch) => {
                setDraftLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
              }}
              onRemoveLine={(index) => {
                setDraftLines((prev) => prev.filter((_, i) => i !== index));
              }}
              extraForm={
                !confirmed ? (
                  <ExtraConceptForm
                    lines={display.lines}
                    onAdd={(line) => {
                      setDraftLines((prev) => [...prev, line]);
                    }}
                  />
                ) : null
              }
            />
            {confirmed && (invoice.changes?.length ?? 0) > 0 ? (
              <AuditedChangesList
                changes={invoice.changes ?? []}
                confirmedAt={invoice.confirmedAt}
                confirmedBy={invoice.confirmedBy}
                locale={i18n.language}
                t={t}
              />
            ) : null}
          </>
        )}
        </div>
      )}
    </ModalShell>
  );
}

const FIELD_CLASS =
  'w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500';

const TEXTAREA_CLASS =
  'min-h-[88px] w-full resize-y rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500';

function changeTypeLabel(
  type: InvoiceLineChange['type'],
  t: (k: string) => string
): string {
  if (type === 'added') return t('pages.clinicInvoices.changeAdded');
  if (type === 'removed') return t('pages.clinicInvoices.changeRemoved');
  return t('pages.clinicInvoices.changeEdited');
}

function changeBadgeClass(type: InvoiceLineChange['type']): string {
  if (type === 'added') return 'bg-teal-100 text-teal-800';
  if (type === 'removed') return 'bg-rose-100 text-rose-800';
  return 'bg-amber-100 text-amber-800';
}

function ChangeLinePreview({
  line,
  emptyLabel,
  locale,
}: {
  line: ClinicInvoiceLine | null;
  emptyLabel: string;
  locale: string;
}) {
  if (!line) {
    return <p className="text-sm italic text-slate-400">{emptyLabel}</p>;
  }
  return (
    <div>
      <p className="text-sm font-medium text-slate-900">{line.name}</p>
      {line.description?.trim() ? (
        <p className="mt-0.5 text-xs text-slate-500">{line.description}</p>
      ) : null}
      <p className="mt-1 text-xs text-slate-600">
        {line.quantity} × {formatMoney(line.unitPrice, locale)} = {formatMoney(line.subtotal, locale)}
      </p>
    </div>
  );
}

function InvoiceChangeReview({
  changes,
  reasons,
  error,
  locale,
  t,
  onReason,
}: {
  changes: InvoiceLineChange<ClinicInvoiceLine>[];
  reasons: Record<string, string>;
  error: string;
  locale: string;
  t: (k: string) => string;
  onReason: (id: string, value: string) => void;
}) {
  return (
    <div className="mx-auto max-w-5xl rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <h3 className="text-lg font-semibold text-slate-900">{t('pages.clinicInvoices.reviewTitle')}</h3>
      <p className="mt-1 text-sm text-slate-600">{t('pages.clinicInvoices.reviewHint')}</p>
      <div className="mt-4 hidden grid-cols-[1fr_1fr_minmax(16rem,1.1fr)] gap-4 px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 lg:grid">
        <span>{t('pages.clinicInvoices.reviewBefore')}</span>
        <span>{t('pages.clinicInvoices.reviewAfter')}</span>
        <span>{t('pages.clinicInvoices.reviewReason')}</span>
      </div>
      <div className="mt-2 space-y-3">
        {changes.map((change) => (
          <div
            key={change.id}
            className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 sm:p-4"
          >
            <span
              className={`mb-3 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${changeBadgeClass(change.type)}`}
            >
              {changeTypeLabel(change.type, t)}
            </span>
            <div className="grid gap-3 lg:grid-cols-[1fr_1fr_minmax(16rem,1.1fr)] lg:items-start">
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 lg:hidden">
                  {t('pages.clinicInvoices.reviewBefore')}
                </p>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <ChangeLinePreview
                    line={change.before}
                    emptyLabel="—"
                    locale={locale}
                  />
                </div>
              </div>
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 lg:hidden">
                  {t('pages.clinicInvoices.reviewAfter')}
                </p>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <ChangeLinePreview
                    line={change.after}
                    emptyLabel="—"
                    locale={locale}
                  />
                </div>
              </div>
              <label className="block">
                <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500 lg:hidden">
                  {t('pages.clinicInvoices.reviewReason')}
                </span>
                <textarea
                  className={TEXTAREA_CLASS}
                  value={reasons[change.id] ?? ''}
                  maxLength={1000}
                  onChange={(e) => onReason(change.id, e.target.value)}
                  placeholder={t('pages.clinicInvoices.reviewReasonPlaceholder')}
                />
              </label>
            </div>
          </div>
        ))}
      </div>
      {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}
    </div>
  );
}

function AuditedChangesList({
  changes,
  confirmedAt,
  confirmedBy,
  locale,
  t,
}: {
  changes: ClinicInvoiceAuditedChange[];
  confirmedAt: string | null;
  confirmedBy: string | null;
  locale: string;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  const at = confirmedAt ? new Date(confirmedAt).toLocaleString(locale) : '—';
  return (
    <div className="mx-auto mt-4 max-w-[210mm] rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <h3 className="text-sm font-semibold text-slate-900">{t('pages.clinicInvoices.auditedTitle')}</h3>
      {confirmedBy ? (
        <p className="mt-0.5 text-xs text-slate-500">
          {t('pages.clinicInvoices.auditedBy', { by: confirmedBy, at })}
        </p>
      ) : null}
      <div className="mt-3 space-y-3">
        {changes.map((change) => (
          <div key={change.id} className="grid gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3 lg:grid-cols-[1fr_1fr_minmax(14rem,1fr)]">
            <div>
              <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${changeBadgeClass(change.type)}`}>
                {changeTypeLabel(change.type, t)}
              </span>
              <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {t('pages.clinicInvoices.reviewBefore')}
              </p>
              <ChangeLinePreview line={change.before} emptyLabel="—" locale={locale} />
            </div>
            <div>
              <p className="mt-6 text-[11px] font-semibold uppercase tracking-wide text-slate-500 lg:mt-7">
                {t('pages.clinicInvoices.reviewAfter')}
              </p>
              <ChangeLinePreview line={change.after} emptyLabel="—" locale={locale} />
            </div>
            <div>
              <p className="mt-6 text-[11px] font-semibold uppercase tracking-wide text-slate-500 lg:mt-7">
                {t('pages.clinicInvoices.reviewReason')}
              </p>
              <p className="text-sm text-slate-800">{change.reason}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function extraUnitPrice(lines: ClinicInvoiceLine[], kind: ClinicInvoiceLineKind, fallback: number): number {
  const found = lines.find((line) => line.kind === kind && line.unitPrice > 0);
  return found?.unitPrice ?? fallback;
}

function ExtraConceptForm({
  lines,
  onAdd,
}: {
  lines: ClinicInvoiceLine[];
  onAdd: (line: ClinicInvoiceLine) => void;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<ClinicInvoiceLineKind>('other');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState(0);
  const [error, setError] = useState('');

  const applyPreset = (next: ClinicInvoiceLineKind) => {
    setKind(next);
    setError('');
    if (next === 'consultorio_extra') {
      setName(t('pages.clinicInvoices.extraOffices'));
      setDescription(t('pages.clinicInvoices.extraOfficeHint'));
      setQuantity(1);
      setUnitPrice(extraUnitPrice(lines, 'consultorio_extra', 15000));
      return;
    }
    if (next === 'profesional_extra') {
      setName(t('pages.clinicInvoices.extraPros'));
      setDescription(t('pages.clinicInvoices.extraProHint'));
      setQuantity(1);
      setUnitPrice(extraUnitPrice(lines, 'profesional_extra', 15000));
      return;
    }
    setName('');
    setDescription('');
    setQuantity(1);
    setUnitPrice(0);
  };

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t('pages.clinicInvoices.addExtraError'));
      return;
    }
    const qty = Math.max(0, quantity);
    const price = unitPrice;
    onAdd({
      kind,
      name: trimmed,
      description: description.trim() || null,
      quantity: qty,
      unitPrice: price,
      subtotal: round2(qty * price),
    });
    setKind('other');
    setName('');
    setDescription('');
    setQuantity(1);
    setUnitPrice(0);
    setError('');
  };

  const presetClass = (active: boolean) =>
    `rounded-full border px-3 py-1 text-xs font-medium ${
      active
        ? 'border-teal-600 bg-teal-600 text-white'
        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
    }`;

  return (
    <div
      id="clinic-invoice-add-extra"
      className="mt-3 rounded-xl border border-dashed border-teal-300 bg-teal-50/40 p-4"
    >
      <h4 className="text-sm font-semibold text-teal-900">
        {t('pages.clinicInvoices.addExtraTitle')}
      </h4>
      <p className="mt-1 text-xs text-slate-600">{t('pages.clinicInvoices.addExtraHint')}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className={presetClass(kind === 'consultorio_extra')} onClick={() => applyPreset('consultorio_extra')}>
          {t('pages.clinicInvoices.extraOffices')}
        </button>
        <button type="button" className={presetClass(kind === 'profesional_extra')} onClick={() => applyPreset('profesional_extra')}>
          {t('pages.clinicInvoices.extraPros')}
        </button>
        <button type="button" className={presetClass(kind === 'other')} onClick={() => applyPreset('other')}>
          {t('pages.clinicInvoices.presetOther')}
        </button>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="sm:col-span-2">
          <span className="mb-1 block text-[11px] font-medium text-slate-500">
            {t('pages.clinicInvoices.concept')}
          </span>
          <input
            className={FIELD_CLASS}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError('');
            }}
            placeholder={t('pages.clinicInvoices.concept')}
          />
        </label>
        <label>
          <span className="mb-1 block text-[11px] font-medium text-slate-500">
            {t('pages.clinicInvoices.qty')}
          </span>
          <input
            type="number"
            min={0}
            step="any"
            className={FIELD_CLASS}
            value={quantity}
            onChange={(e) => setQuantity(Number(e.target.value) || 0)}
          />
        </label>
        <label>
          <span className="mb-1 block text-[11px] font-medium text-slate-500">
            {t('pages.clinicInvoices.unitPrice')}
          </span>
          <input
            type="number"
            step="0.01"
            className={FIELD_CLASS}
            value={unitPrice}
            onChange={(e) => setUnitPrice(Number(e.target.value) || 0)}
          />
        </label>
        <label className="sm:col-span-2 lg:col-span-3">
          <span className="mb-1 block text-[11px] font-medium text-slate-500">
            {t('pages.clinicInvoices.descriptionPlaceholder')}
          </span>
          <input
            className={FIELD_CLASS}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('pages.clinicInvoices.descriptionPlaceholder')}
          />
        </label>
        <div className="flex items-end">
          <Button className="w-full" onClick={submit}>
            {t('pages.clinicInvoices.addConcept')}
          </Button>
        </div>
      </div>
      {error ? <p className="mt-2 text-xs text-rose-600">{error}</p> : null}
    </div>
  );
}

